import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "./api";
import { useAuth } from "./auth";

const ActivityContext = createContext(null);
const POLL_MS = 30000;

// Real, backend-backed activity for the signed-in user: joined rooms + unread notifications.
export function ActivityProvider({ children }) {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [loadedFor, setLoadedFor] = useState(null); // user id the last completed refresh belongs to
  // Joins confirmed in this session before /me/bhega reflects them (or never will: "pending" joins).
  const [localJoins, setLocalJoins] = useState({}); // { [productId]: "YYYY-MM" | "ASAP" | "pending" }

  const refresh = useCallback(async () => {
    if (!user || user.role === "admin") { setRooms([]); setNotifications([]); setTotalUnread(0); setLoaded(true); setLoadedFor(user?.id ?? null); return; }
    try {
      const [b, n] = await Promise.all([api.get("/me/bhega"), api.get("/me/notifications")]);
      setRooms((b.data.active || b.data.items || []).filter((it) => (it.city || "").trim()));
      setNotifications(n.data.items || []);
      setTotalUnread(n.data.total_unread || 0);
    } catch (_) { /* 401 handled globally */ }
    setLoaded(true);
    setLoadedFor(user?.id ?? null);
  }, [user]);

  // A different (or no) user: forget local joins.
  useEffect(() => { setLocalJoins({}); }, [user?.id]);

  const markJoined = useCallback((pid, month) => {
    if (!pid) return;
    setLocalJoins((cur) => ({ ...cur, [pid]: month || "pending" }));
  }, []);

  // Forget a local join (e.g. after leaving the room) so joinedIds falls back to the server list.
  const unmarkJoined = useCallback((pid) => {
    setLocalJoins((cur) => {
      if (!pid || !(pid in cur)) return cur;
      const next = { ...cur };
      delete next[pid];
      return next;
    });
  }, []);

  useEffect(() => {
    setLoaded(false);
    refresh();
    if (!user) return undefined;
    const id = setInterval(refresh, POLL_MS);
    const onChange = () => refresh();
    window.addEventListener("bhega:changed", onChange);
    return () => { clearInterval(id); window.removeEventListener("bhega:changed", onChange); };
  }, [user, refresh]);

  const markAllRead = useCallback(async () => {
    await api.post("/me/notifications/read-all");
    refresh();
  }, [refresh]);

  const joinedIds = useMemo(
    () => new Set([...rooms.map((r) => r.product_id), ...Object.keys(localJoins)]),
    [rooms, localJoins],
  );

  return (
    <ActivityContext.Provider value={{ rooms, notifications, totalUnread, joinedIds, localJoins, markJoined, unmarkJoined, loaded, loadedFor, refresh, markAllRead }}>
      {children}
    </ActivityContext.Provider>
  );
}

export function useActivity() {
  return useContext(ActivityContext);
}

export function openAuth() {
  window.dispatchEvent(new CustomEvent("bhega:open-auth"));
}

export function fmtMonth(d) {
  if (!d) return "";
  const s = String(d);
  if (s.toLowerCase() === "asap") return "ASAP";
  if (/^\d{4}-\d{2}/.test(s)) return new Date(`${s.slice(0, 7)}-01`).toLocaleDateString("en-IN", { month: "short", year: "numeric" });
  return new Date(s).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" });
}

export function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso)) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 30 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}
